import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useHistory } from "react-router-dom";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import useBasePath from "src/utils/hooks/useBasePath";

import {
  IAbsenceRequest,
  IAbsenceRequestDTO,
  IAbsenceRequestHistory,
  IAbsenceRequestsWithETags,
  IAbsenceRequestListItem,
  IRequireCorrectionDTO,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  acceptAbsenceRequests,
  cancelAbsenceRequest,
  createAbsenceRequest,
  editAbsenceRequest,
  fetchAbsenceRequestHistory,
  fetchEmployeeAbsenceRequest,
  fetchEmployeeAbsenceRequests,
  fetchManagedAbsenceRequests,
  rejectAbsenceRequests,
  requireCorrectionAbsenceRequest,
  ResponseTypeList,
} from "./api";
import { AbsenceQueryKeys } from "./config";

const AbsenceRequestsQueries = {
  employeeList: (params: {
    employeeId: string;
    page: number;
    size: number;
    sort?: string[];
    filters?: { field: string; value: string | string[] }[];
  }) =>
    queryOptions<ResponseTypeList<IAbsenceRequestListItem>, Error>({
      queryKey: AbsenceQueryKeys.employeeAbsenceRequests(params),
      queryFn: ({ signal }) =>
        fetchEmployeeAbsenceRequests({ ...params, signal }),
    }),

  managedList: (params: {
    companyId: string;
    page: number;
    size: number;
    sort?: string[];
    filters?: { field: string; value: string | string[] }[];
  }) =>
    queryOptions<ResponseTypeList<IAbsenceRequestListItem>, Error>({
      queryKey: AbsenceQueryKeys.managedAbsenceRequests(params),
      queryFn: ({ signal }) =>
        fetchManagedAbsenceRequests({ ...params, signal }),
    }),

  details: (absenceRequestId: string) =>
    queryOptions<IAbsenceRequest, Error>({
      queryKey: AbsenceQueryKeys.employeeAbsenceRequest(absenceRequestId),
      queryFn: ({ signal }) =>
        fetchEmployeeAbsenceRequest(absenceRequestId, signal),
    }),

  history: (absenceRequestId: string) =>
    queryOptions<IAbsenceRequestHistory>({
      enabled: !!absenceRequestId,
      queryKey: AbsenceQueryKeys.absenceRequestHistory(absenceRequestId),
      queryFn: ({ signal }) =>
        fetchAbsenceRequestHistory(absenceRequestId, signal),
    }),

  accept: mutationOptions({
    mutationFn: ({
      absenceRequests,
      absenceRequestsWithETags,
    }: {
      absenceRequests: string[];
      absenceRequestsWithETags: IAbsenceRequestsWithETags[];
    }) => acceptAbsenceRequests(absenceRequests, absenceRequestsWithETags),
  }),

  reject: mutationOptions({
    mutationFn: ({
      absenceRequests,
      absenceRequestsWithETags,
    }: {
      absenceRequests: string[];
      absenceRequestsWithETags: IAbsenceRequestsWithETags[];
    }) => rejectAbsenceRequests(absenceRequests, absenceRequestsWithETags),
  }),

  cancel: mutationOptions({
    mutationFn: ({ absenceRequestId, eTag }: IAbsenceRequestsWithETags) =>
      cancelAbsenceRequest(absenceRequestId, eTag),
  }),

  requireCorrection: (absenceRequestId: string) =>
    mutationOptions({
      mutationFn: ({
        data,
        eTag,
      }: {
        data: IRequireCorrectionDTO;
        eTag: string;
      }) => requireCorrectionAbsenceRequest(absenceRequestId, data, eTag),
    }),
};

export const useFetchEmployeeAbsenceRequests = (params: {
  employeeId: string;
  page: number;
  size: number;
  sort?: string[];
  filters?: { field: string; value: string | string[] }[];
}) => useQuery(AbsenceRequestsQueries.employeeList(params));

export const useFetchManagedAbsenceRequests = (params: {
  companyId: string;
  page: number;
  size: number;
  sort?: string[];
  filters?: { field: string; value: string | string[] }[];
}) => useQuery(AbsenceRequestsQueries.managedList(params));

export const useFetchEmployeeAbsenceRequest = (absenceRequestId: string) =>
  useQuery(AbsenceRequestsQueries.details(absenceRequestId));

export const useFetchAbsenceRequestHistory = (absenceRequestId: string) =>
  useQuery(AbsenceRequestsQueries.history(absenceRequestId));

export const useMutateAbsenceRequest = (
  employeeId: string,
  absenceRequestId?: string
) => {
  const history = useHistory();
  const basePath = useBasePath();
  const queryClient = useQueryClient();

  return useMutation<IAbsenceRequestDTO, AxiosError, IAbsenceRequestDTO>({
    mutationFn: (data) =>
      absenceRequestId
        ? editAbsenceRequest(absenceRequestId, data)
        : createAbsenceRequest(data),
    onSuccess: () => {
      const message = absenceRequestId
        ? "Zmiany we wniosku zostały poprawnie zapisane."
        : "Wniosek został pomyślnie złożony.";

      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.employeeAbsenceRequestAll(),
      });
      dynamicNotification(__(message), "success");
      setTimeout(() => {
        history.push(`${basePath}/absences/employee/${employeeId}#requests`);
      }, 500);
    },
    onError: () => {
      const message = absenceRequestId
        ? "Wystąpił błąd. Nie udało się zapisać zmian we wniosku."
        : "Wystąpił błąd. Nie udało się złożyć wniosku.";
      dynamicNotification(__(message), "error", "top-right", 7000);
    },
  });
};

export const useAcceptAbsenceRequests = (resetModal?: VoidFunction) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...AbsenceRequestsQueries.accept,
    onSuccess: () => {
      dynamicNotification(
        __("Wniosek został pomyślnie zaakceptowany."),
        "success"
      );
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.employeeAbsenceRequestsAll(),
      });
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.managedAbsenceRequestsAll(),
      });
      if (typeof resetModal === "function") resetModal();
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się zaakceptować wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useRejectAbsenceRequests = (resetModal: VoidFunction) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...AbsenceRequestsQueries.reject,
    onSuccess: () => {
      dynamicNotification(__("Wniosek został pomyślnie odrzucony."), "success");
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.employeeAbsenceRequestsAll(),
      });
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.managedAbsenceRequestsAll(),
      });
      resetModal();
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się odrzucić wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useRequireCorrectionAbsenceRequest = (
  absenceRequestId: string,
  resetModal: VoidFunction
) => {
  const queryClient = useQueryClient();

  return useMutation<
    { data: IRequireCorrectionDTO; eTag: string },
    AxiosError,
    { data: IRequireCorrectionDTO; eTag: string }
  >({
    ...AbsenceRequestsQueries.requireCorrection(absenceRequestId),
    onSuccess: () => {
      dynamicNotification(
        __("Wniosek został pomyślnie przekazany do poprawy."),
        "success"
      );
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.employeeAbsenceRequestsAll(),
      });
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.managedAbsenceRequestsAll(),
      });
      resetModal();
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się przekazać do poprawy wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useCancelAbsenceRequests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...AbsenceRequestsQueries.cancel,
    onSuccess: () => {
      dynamicNotification(__("Wniosek został pomyślnie anulowany."), "success");
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.employeeAbsenceRequestsAll(),
      });
      queryClient.invalidateQueries({
        queryKey: AbsenceQueryKeys.managedAbsenceRequestsAll(),
      });
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się anulować wniosku."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export default AbsenceRequestsQueries;
